import React, { useMemo } from 'react';

import type {
  ComponentRenderProps,
  TableProps,
} from '../../../types/dynamic_views';
import { useManifestId } from '../ManifestContext';
import { renderTextWithIcons } from '../utils/emojiToIcon';

/**
 * Class for an emoji-derived icon rendered INSIDE a run of text. The trailing
 * `mr-1` is inline spacing within a text flow — there is no flex parent here to
 * carry a `gap`, so this is the one sanctioned margin (VISUAL_STYLE_GUIDE.md
 * § Spacing allows margins for text stacks). Hoisted so the seven call sites
 * below can't drift apart.
 */
const INLINE_ICON_CLASS = 'w-4 h-4 inline-block relative -top-[2px] mr-1';

/**
 * Renders cell content with support for markdown-style links and emoji-to-icon conversion.
 * Converts [text](url) syntax to clickable links and emojis to Untitled UI icons.
 */
const renderCellContent = (
  content: string,
  manifestId?: string
): React.ReactNode => {
  if (!content || typeof content !== 'string') {
    return content;
  }

  // Match markdown links: [text](url)
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;

  while ((match = linkRegex.exec(content)) !== null) {
    // Add text before the link (with emoji conversion)
    if (match.index > lastIndex) {
      const textBefore = content.slice(lastIndex, match.index);
      parts.push(
        <React.Fragment key={`text-${lastIndex}`}>
          {renderTextWithIcons(textBefore, INLINE_ICON_CLASS, manifestId)}
        </React.Fragment>
      );
    }

    // Add the link (only if URL is safe - must start with http:// or https://)
    const [, linkText, url] = match;
    if (!/^https?:\/\//i.test(url)) {
      // Unsafe URL (e.g., javascript:) - render as plain text with emoji conversion
      parts.push(
        <React.Fragment key={`unsafe-${match.index}`}>
          {renderTextWithIcons(match[0], INLINE_ICON_CLASS, manifestId)}
        </React.Fragment>
      );
    } else {
      parts.push(
        <a
          key={match.index}
          href={url}
          target='_blank'
          rel='noopener noreferrer'
          className='text-fg-primary hover:text-fg-secondary underline'
          onClick={e => e.stopPropagation()}
        >
          {renderTextWithIcons(linkText, INLINE_ICON_CLASS, manifestId)}
        </a>
      );
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after the last link (with emoji conversion)
  if (lastIndex < content.length) {
    const textAfter = content.slice(lastIndex);
    parts.push(
      <React.Fragment key={`text-${lastIndex}`}>
        {renderTextWithIcons(textAfter, INLINE_ICON_CLASS, manifestId)}
      </React.Fragment>
    );
  }

  // If no links were found, just return with emoji conversion
  return parts.length > 0
    ? parts
    : renderTextWithIcons(content, INLINE_ICON_CLASS, manifestId);
};

export const TableComponent: React.FC<ComponentRenderProps> = ({
  component,
  className = '',
}) => {
  const props = component.props as TableProps;
  const {
    headers,
    rows,
    striped = true,
    compact = false,
    bordered = true,
    caption,
  } = props;
  const manifestId = useManifestId();

  // Build CSS classes - using design system tokens for dark mode support
  const tableClasses = useMemo(
    () =>
      [
        'w-full text-[14px]',
        bordered
          ? 'border border-border-secondary rounded-xl overflow-hidden'
          : '',
        className,
      ]
        .filter(Boolean)
        .join(' '),
    [bordered, className]
  );

  // Using design system tokens - no hover/click states by default
  const headerClasses = useMemo(
    () =>
      [
        'bg-bg-secondary text-fg-primary text-[13px] font-medium text-left',
        compact ? 'px-3 py-2' : 'px-3 py-2.5',
        bordered ? 'border-b border-border-secondary' : '',
      ]
        .filter(Boolean)
        .join(' '),
    [bordered, compact]
  );

  const cellClasses = useMemo(
    () =>
      [
        'text-[13px] text-fg-primary',
        compact ? 'px-3 py-1.5' : 'px-3 py-2',
        bordered ? 'border-b border-border-secondary' : '',
      ]
        .filter(Boolean)
        .join(' '),
    [bordered, compact]
  );

  // Coerce non-string headers/cells to strings (LLM may emit objects or numbers)
  const normalizedHeaders: string[] = useMemo(
    () =>
      (Array.isArray(headers) ? headers : []).map(h => {
        if (typeof h === 'string') {
          return h;
        }
        if (typeof h === 'object' && h !== null) {
          return (
            (h as Record<string, string>).label ??
            (h as Record<string, string>).key ??
            JSON.stringify(h)
          );
        }
        return String(h);
      }),
    [headers]
  );

  // Validate data
  if (!normalizedHeaders || normalizedHeaders.length === 0) {
    return (
      <div className='border border-error-200 bg-error-50 p-4 rounded'>
        <div className='text-error-700 font-medium'>Invalid table data</div>
        <div className='text-error-600 text-sm'>
          Headers must be a non-empty array
        </div>
      </div>
    );
  }

  if (!rows || !Array.isArray(rows)) {
    return (
      <div className='border border-warning-200 bg-warning-50 p-4 rounded'>
        <div className='text-warning-700 font-medium'>No table data</div>
        <div className='text-warning-600 text-sm'>
          No rows provided for the table
        </div>
      </div>
    );
  }

  return (
    <div
      className='table-container overflow-x-auto'
      data-component-id={component.id}
    >
      {typeof caption === 'string' && caption && (
        <div className='table-caption text-[14px] text-fg-quaternary mb-2 font-medium'>
          {renderTextWithIcons(caption, INLINE_ICON_CLASS, manifestId)}
        </div>
      )}
      <table className={tableClasses}>
        <thead>
          <tr>
            {(() => {
              const seen = new Map<string, number>();
              return normalizedHeaders.map(header => {
                const count = seen.get(header) ?? 0;
                seen.set(header, count + 1);
                return (
                  <th key={`${header}::${count}`} className={headerClasses}>
                    {renderTextWithIcons(header, INLINE_ICON_CLASS, manifestId)}
                  </th>
                );
              });
            })()}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr
              // eslint-disable-next-line react/no-array-index-key -- Table rows have no stable unique identifier
              key={`row-${rowIndex}`}
              className={
                striped && rowIndex % 2 === 1
                  ? 'bg-bg-secondary'
                  : 'bg-bg-primary'
              }
            >
              {(() => {
                const cellSeen = new Map<string, number>();
                return normalizedHeaders.map((header, colIndex) => {
                  const headerObj = headers[colIndex];
                  const key =
                    typeof headerObj === 'object' && headerObj !== null
                      ? ((headerObj as Record<string, string>)?.key ?? header)
                      : header;
                  let rawCell: unknown;
                  if (row == null) {
                    rawCell = undefined;
                  } else if (Array.isArray(row)) {
                    rawCell = row[colIndex];
                  } else {
                    const rowObj = row as Record<string, unknown>;
                    rawCell = rowObj[key];
                    if (rawCell === undefined) {
                      const lowerKey = String(key).toLowerCase();
                      const matchedKey = Object.keys(rowObj).find(
                        k => k.toLowerCase() === lowerKey
                      );
                      rawCell =
                        matchedKey !== undefined
                          ? rowObj[matchedKey]
                          : undefined;
                    }
                  }
                  let cellValue: string;
                  if (typeof rawCell === 'string') {
                    cellValue = rawCell;
                  } else if (rawCell == null) {
                    cellValue = '';
                  } else {
                    cellValue = String(rawCell);
                  }
                  const hdrDupCount = cellSeen.get(header) ?? 0;
                  cellSeen.set(header, hdrDupCount + 1);
                  return (
                    <td
                      key={`${header}::${hdrDupCount}`}
                      className={cellClasses}
                    >
                      {renderCellContent(cellValue, manifestId)}
                    </td>
                  );
                });
              })()}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TableComponent;
